import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Warehouse,
  FileText,
  CreditCard,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  Box,
  DollarSign,
  BarChart3,
  TrendingDown,
  FileSpreadsheet,
  Link2,
  RotateCcw
} from 'lucide-react';

// Navigation items with module mappings
const navigation = [
  { name: 'Dashboard', path: '/', icon: LayoutDashboard, module: 'dashboard' },
  { name: 'SKU Master', path: '/skus', icon: Package, module: 'sku_master' },
  { name: 'Procurement', path: '/procurement', icon: ShoppingCart, module: 'procurement' },
  { name: 'Packing', path: '/packing', icon: Box, module: 'packing' },
  { name: 'Inventory', path: '/inventory', icon: Warehouse, module: 'inventory' },
  { name: 'Stock Adjustments', path: '/stock-adjustments', icon: RotateCcw, module: 'inventory' },
  { name: 'Transfers', path: '/transfers', icon: FileText, module: 'transfers' },
  { name: 'Analytics', path: '/analytics', icon: BarChart3, module: 'analytics' },
  { type: 'separator', label: 'Platform' },
  { name: 'Blinkit', path: '/blinkit', icon: FileSpreadsheet, module: 'platform_blinkit' },
  { name: 'Instamart', path: '/instamart', icon: FileSpreadsheet, module: 'platform_blinkit' },
  { name: 'Zepto', path: '/zepto', icon: FileSpreadsheet, module: 'platform_blinkit' },
  { name: 'SKU Mapping', path: '/sku-mapping', icon: Link2, module: 'platform_blinkit' },
  { type: 'separator', label: 'Finance' },
  { name: 'Ads Wallet', path: '/ads', icon: DollarSign, module: 'ads_wallet' },
  { name: 'Payments', path: '/payments', icon: CreditCard, module: 'payments' },
  { name: 'Platform Payouts', path: '/platform-payouts', icon: TrendingDown, module: 'platform_payouts' },
  { name: 'Settings', path: '/settings', icon: Settings, module: 'admin' }
];

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, hasModule } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter navigation based on user's allowed modules
  const filteredNavigation = navigation.filter(item => 
    item.type === 'separator' || hasModule(item.module)
  );

  // Remove separators that have no items after them
  const cleanedNavigation = filteredNavigation.filter((item, idx) => {
    if (item.type !== 'separator') return true;
    // Check if next non-separator item exists
    const nextItem = filteredNavigation.slice(idx + 1).find(n => n.type !== 'separator');
    return nextItem != null;
  });

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-slate-200">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4 mb-5">
              <div className="h-10 w-10 bg-indigo-700 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-white" />
              </div>
              <div className="ml-3">
                <span className="text-lg font-heading font-bold text-slate-900">Sattvik Earth</span>
                <p className="text-xs text-slate-500">Powered by ig16</p>
              </div>
            </div>
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {cleanedNavigation.map((item, idx) => {
                if (item.type === 'separator') {
                  return (
                    <div key={`sep-${item.label}`} className="pt-3 pb-1 px-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{item.label}</p>
                    </div>
                  );
                }
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      active
                        ? 'bg-indigo-50 text-indigo-700 border-l-2 border-indigo-700'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${active ? 'text-indigo-700' : 'text-slate-400'}`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-slate-200 p-4">
            <div className="flex items-center w-full">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="ml-3 p-2 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-50"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white border-b border-slate-200">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="px-4 text-slate-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <div className="flex-1 flex items-center justify-between px-4">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-indigo-700 rounded-lg flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
            <span className="ml-2 text-lg font-heading font-bold text-slate-900">Sattvik Earth</span>
          </div>
          <button
            data-testid="mobile-logout-button"
            onClick={handleLogout}
            className="p-2 rounded-md text-slate-400 hover:text-slate-600"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-slate-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <nav className="mt-5 px-2 space-y-1">
                {cleanedNavigation.map((item, idx) => {
                  if (item.type === 'separator') {
                    return (
                      <div key={`msep-${item.label}`} className="pt-3 pb-1 px-3">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{item.label}</p>
                      </div>
                    );
                  }
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                        active
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className={`mr-3 h-5 w-5 ${active ? 'text-indigo-700' : 'text-slate-400'}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="md:pl-64 flex flex-col flex-1">
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 z-10">
          <div className="flex justify-around items-center">
            {filteredNavigation.slice(0, 4).map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex flex-col items-center justify-center px-3 py-2 rounded-md ${
                    active ? 'text-indigo-700' : 'text-slate-400'
                  }`}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-xs mt-1">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
